/**
 * The availability question, answered in a sentence.
 *
 * P-Q013 returns free and blocked intervals; reading them is work a customer
 * should not have to do to learn whether their dates are possible. This states
 * the answer and leaves the intervals as the detail behind it.
 *
 * BR-9.8 still applies and is still shown beside this everywhere it is used:
 * availability is informational until a hold exists. The wording deliberately
 * says "looks free", never "is reserved".
 *
 * `from` and `to` are plain `YYYY-MM-DD` dates — what a date control gives.
 */
export function availabilityAnswer(availability, from, to) {
    const start = Date.parse(`${from}T00:00:00.000Z`);
    const end = Date.parse(`${to}T23:59:59.000Z`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
        return { tone: 'neutral', label: 'Choose the dates you need' };
    }
    const overlaps = (a, b) => Date.parse(a) < end && Date.parse(b) > start;
    const clash = availability.blocked.some((interval) => overlaps(interval.start, interval.end));
    if (clash)
        return { tone: 'warning', label: 'Not free for all of these dates' };
    const covered = availability.free.some((interval) => Date.parse(interval.start) <= start && Date.parse(interval.end) >= end);
    if (covered)
        return { tone: 'success', label: 'Looks free for your dates' };
    return { tone: 'neutral', label: 'Partly free in this window' };
}
/**
 * A model specification, as text.
 *
 * The registry types each attribute, so exactly one of the value columns is
 * populated. Reading the wrong one prints "null"; reading them in order prints
 * the value the catalogue actually holds.
 */
export function specificationValue(spec) {
    if (spec.value_text !== null)
        return spec.value_text;
    if (spec.value_enum_code !== null)
        return spec.value_enum_code;
    if (spec.value_boolean !== null)
        return spec.value_boolean ? 'Yes' : 'No';
    if (spec.value_numeric !== null) {
        return spec.unit_code === null ? spec.value_numeric : `${spec.value_numeric} ${spec.unit_code}`;
    }
    return null;
}
/**
 * Which published rate a screen leads with.
 *
 * A marketplace has to answer "what does this cost" above the fold, and a table
 * of four period rates does not answer it. This picks ONE to promote — the
 * daily rate where the owner published one, otherwise the first row of the card
 * as the owner ordered it.
 *
 * It is a selection, not a calculation. No weekly rate is divided into days, no
 * cheapest row is worked out, and nothing is summed: every one of those would
 * be a pricing rule the API never stated. What comes back is a row the owner
 * published, verbatim, for a caller to typeset.
 */
export function headlineRate(rateCard) {
    const rates = rateCard.period_rates ?? [];
    if (rates.length === 0)
        return null;
    return rates.find((rate) => rate.period_type === 'daily') ?? rates[0] ?? null;
}
//# sourceMappingURL=discovery.js.map